// Format: [text](url) or plain URLs
export const parseHyperlinks = (text: string): (string | { text: string; url: string })[] => {
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
  const parts: (string | { text: string; url: string })[] = [];
  let lastIndex = 0;

  // First pass: parse markdown links
  const segments: Array<{ start: number; end: number; text: string; url: string; isMarkdown: boolean }> = [];
  let match;

  while ((match = markdownLinkRegex.exec(text)) !== null) {
    segments.push({
      start: match.index,
      end: match.index + match[0].length,
      text: match[1],
      url: match[2],
      isMarkdown: true,
    });
  }

  // Second pass: parse plain URLs (but skip areas already covered by markdown links)
  while ((match = urlRegex.exec(text)) !== null) {
    const matchStart = match.index;
    const matchEnd = match.index + match[0].length;

    // Check if this URL is inside a markdown link
    const insideMarkdownLink = segments.some((seg) => seg.isMarkdown && matchStart >= seg.start && matchEnd <= seg.end);

    if (!insideMarkdownLink) {
      let url = match[0];
      // Add https:// prefix if URL starts with www.
      if (url.startsWith("www.")) {
        url = "https://" + url;
      }

      segments.push({
        start: matchStart,
        end: matchEnd,
        text: match[0],
        url: url,
        isMarkdown: false,
      });
    }
  }

  // Sort segments by start position
  segments.sort((a, b) => a.start - b.start);

  // Build the final parts array
  segments.forEach((segment) => {
    // Add text before this segment
    if (segment.start > lastIndex) {
      parts.push(text.substring(lastIndex, segment.start));
    }

    // Add the link
    parts.push({
      text: segment.text,
      url: segment.url,
    });

    lastIndex = segment.end;
  });

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

export const serializeHyperlink = (text: string, url: string): string => {
  return `[${text}](${url})`;
};

export const extractTextFromContentEditable = (element: HTMLDivElement): string => {
  let text = "";
  element.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.classList.contains("hyperlink")) {
        const linkText = el.textContent || "";
        const url = el.getAttribute("data-url") || "";
        text += serializeHyperlink(linkText, url);
      } else if (el.classList.contains("mention")) {
        const userId = el.getAttribute("data-user-id");
        const displayName = el.textContent?.replace("@", "") || "";
        text += `@[${displayName}](${userId})`;
      } else {
        text += el.textContent;
      }
    }
  });
  return text;
};

export const getSelectionInfo = (element: HTMLElement) => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  const selectedText = range.toString();

  // Check if selection is within the element
  if (!element.contains(range.commonAncestorContainer)) return null;

  return {
    range,
    selectedText,
    hasSelection: selectedText.length > 0,
  };
};
